import React, { useState } from 'react'
import Modal from '../Modal'
import { AutoColumn } from '../Column'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { RowBetween } from '../Row'
import { TYPE, CloseIcon } from '../../theme'
import { ButtonError } from '../Button'
import { StakingInfo } from '../../state/stake/hooks'
import { useStakingContract } from '../../hooks/useContract'
import { SubmittedView, LoadingView } from '../ModalViews'
import { TransactionResponse } from '@ethersproject/providers'
import { useTransactionAdder } from '../../state/transactions/hooks'
import FormattedCurrencyAmount from '../FormattedCurrencyAmount'
import { useActiveWeb3React } from '../../hooks'

const ContentWrapper = styled(AutoColumn)`
  width: 100%;
  padding: 1rem;
`

interface StakingModalProps {
  isOpen: boolean
  onDismiss: () => void
  stakingInfo: StakingInfo
  rewardCoin?: string
}

export default function UnstakingModal({ isOpen, onDismiss, stakingInfo, rewardCoin }: StakingModalProps) {
  const { account } = useActiveWeb3React()
  const { tokens } = stakingInfo
  const token0 = stakingInfo.tokens[0]
  // monitor call to help UI loading state
  const addTransaction = useTransactionAdder()
  const [hash, setHash] = useState<string | undefined>()
  const [attempting, setAttempting] = useState(false)

  function wrappedOndismiss() {
    setHash(undefined)
    setAttempting(false)
    onDismiss()
  }

  const stakingContract = useStakingContract(stakingInfo.stakingRewardAddress)

  async function onWithdraw() {
    if (stakingContract && stakingInfo?.stakedAmount) {
      setAttempting(true)
      await stakingContract
        .exit({ gasLimit: 500000 })
        .then((response: TransactionResponse) => {
     
          addTransaction(response, {
            summary: `Withdraw deposited liquidity`
          })
          setHash(response.hash)
        })
        .catch((error: any) => {
          setAttempting(false)
          console.log(error)
        })
    }
  }

  const { t } = useTranslation()

  let error: string | undefined
  if (!account) {
    error = 'Connect Wallet'
  }
  if (!stakingInfo?.stakedAmount) {
    error = error ?? t('enterAnAmount')
  }

  return (
    <Modal isOpen={isOpen} onDismiss={wrappedOndismiss} maxHeight={90}>
      {!attempting && !hash && (
        <ContentWrapper gap="lg">
          <RowBetween>
            <TYPE.mediumHeader>{t('Withdraw')}</TYPE.mediumHeader>
            <CloseIcon onClick={wrappedOndismiss} />
          </RowBetween>
          {stakingInfo?.stakedAmount && (
            <AutoColumn justify="center" gap="md">
              <TYPE.body fontWeight={600} fontSize={36}>
                {<FormattedCurrencyAmount currencyAmount={stakingInfo.stakedAmount} significantDigits={6} />}
              </TYPE.body>
              <TYPE.body>{tokens.length !== 2 ? t('Staked Capital') : t('Deposited liquidity')}:</TYPE.body>
            </AutoColumn>
          )}
          {stakingInfo?.earnedAmount && (
            <AutoColumn justify="center" gap="md">
              <TYPE.body fontWeight={600} fontSize={36}>
                {<FormattedCurrencyAmount currencyAmount={stakingInfo?.earnedAmount} />}
              </TYPE.body>
              <TYPE.body>
                {t('Unclaimed')} {tokens.length !== 2 ? (rewardCoin||tokens[0].symbol) : (stakingInfo?.type === token0?.symbol ? 'CRET' :rewardCoin)}
              </TYPE.body>
            </AutoColumn>
          )}
          <TYPE.subHeader style={{ textAlign: 'center' }}>
            {tokens.length !== 2
              ? t('You are not able to withdraw staked funds before the staking period ends')
              : t('When you withdraw, your AIA is claimed and your liquidity is removed from the mining pool.', {currency: tokens.length !== 2 ? tokens[0].symbol : (stakingInfo?.type === token0?.symbol ? 'CRET' : rewardCoin)})}
          </TYPE.subHeader>
          <ButtonError disabled={!!error} error={!!error && !!stakingInfo?.stakedAmount} onClick={onWithdraw}>
            {error ?? t('Withdraw & Claim')}
          </ButtonError>
        </ContentWrapper>
      )}
      {attempting && !hash && (
        <LoadingView onDismiss={wrappedOndismiss}>
          <AutoColumn gap="12px" justify={'center'}>
            <TYPE.body fontSize={20}>
              Withdrawing {stakingInfo?.stakedAmount?.toSignificant(10)}{' '}
              {tokens.length !== 2 ? tokens[0].symbol : (stakingInfo?.type === token0?.symbol ? 'CRET' :rewardCoin)}-V2
            </TYPE.body>
            <TYPE.body fontSize={20}>
              {t('Claiming')} {stakingInfo?.earnedAmount?.toSignificant(10)} {tokens.length !== 2 ? (rewardCoin||tokens[0].symbol) : (stakingInfo?.type === token0?.symbol ? 'CRET' :rewardCoin)}
            </TYPE.body>
          </AutoColumn>
        </LoadingView>
      )}
      {hash && (
        <SubmittedView onDismiss={wrappedOndismiss} hash={hash}>
          <AutoColumn gap="12px" justify={'center'}>
            <TYPE.largeHeader>{t('Transaction Submitted')}</TYPE.largeHeader>
            <TYPE.body fontSize={20}>Withdrew {tokens.length !== 2 ? (rewardCoin||tokens[0].symbol) : (stakingInfo?.type === token0?.symbol ? 'CRET' :rewardCoin)}-V2!</TYPE.body>
            <TYPE.body fontSize={20}>{t('Claimed')} {tokens.length !== 2 ?(rewardCoin||tokens[0].symbol)  : (stakingInfo?.type === token0?.symbol ? 'CRET' :rewardCoin)}!</TYPE.body>
          </AutoColumn>
        </SubmittedView>
      )}
    </Modal>
  )
}
